/**
 * generateThematicDocs.ts - AutoDocs thématiques transverses Microsoft 365 + Azure.
 *
 *   - Configuration MFA actuelle (identité & authentification)
 *   - État du chiffrement des données (au repos & en transit)
 *
 * Ces documents agrègent, par mots-clés, les contrôles pertinents des catalogues
 * M365 et Azure et y injectent le statut du dernier scan (buildScanByControl
 * fusionne déjà M365 + Azure dans une seule Map, clé = controlId en minuscules).
 */
import { Document, Packer, Paragraph, Table, TableRow, WidthType, HeadingLevel, TextRun } from 'docx';
import { saveAs } from 'file-saver';
import { controls as M365_CONTROLS } from '../domain/controls';
import { AZURE_CONTROLS } from '../domain/azureControls';
import {
  AUTODOC_STYLES, buildCover, buildToc, buildIntro, cell, headerCell, statusFr, statusColor, safeName,
} from './autodocCommon';
import type { AutodocMeta, AutodocHit } from './generateAutodocReports';
import type { MfaFacts } from '../api/mfaFactsApi';

type ScanMap = Map<string, AutodocHit>;

interface ThemeRow { id: string; title: string; severity: string; hit?: AutodocHit; }

function para(text: string, size = 20): Paragraph {
  return new Paragraph({ children: [new TextRun({ text, size })] });
}

/** Sélectionne les contrôles d'un catalogue dont le titre/description matche le thème. */
function pickRows(
  catalog: ReadonlyArray<{ id: string; title: string; description?: string; severity: string }>,
  match: RegExp,
  scanByControl: ScanMap,
): ThemeRow[] {
  return catalog
    .filter((c) => match.test(`${c.title} ${c.description ?? ''}`))
    .map((c) => ({ id: c.id, title: c.title, severity: c.severity, hit: scanByControl.get(c.id.toLowerCase()) }));
}

function controlTable(rows: ThemeRow[]): Table {
  return new Table({
    width: { size: 100, type: WidthType.PERCENTAGE },
    rows: [
      new TableRow({ tableHeader: true, children: [
        headerCell('ID', 12), headerCell('Contrôle', 40), headerCell('Sévérité', 12), headerCell('État', 14), headerCell('Constat', 22),
      ] }),
      ...rows.map((r) => new TableRow({ children: [
        cell(r.id, { bold: true }),
        cell(r.title),
        cell(r.severity),
        cell(statusFr(r.hit?.status), { bold: true, color: statusColor(r.hit?.status) }),
        cell(r.hit?.message?.trim() || (r.hit ? '—' : 'Non couvert par le dernier scan')),
      ] })),
    ],
  });
}

/** Compte les statuts d'un ensemble de lignes (pour la synthèse d'en-tête). */
function summarize(rows: ThemeRow[]): string {
  const c = { pass: 0, fail: 0, warn: 0, manual: 0, other: 0 };
  for (const r of rows) {
    const s = (r.hit?.status ?? '').toLowerCase();
    if (s === 'pass') c.pass++;
    else if (s === 'fail' || s === 'error') c.fail++;
    else if (s === 'warning' || s === 'warn') c.warn++;
    else if (s === 'manual' || s === 'skipped') c.manual++;
    else c.other++;
  }
  return `Conformes : ${c.pass} · Non conformes : ${c.fail} · À améliorer : ${c.warn} · À vérifier : ${c.manual} · Autres/non évalués : ${c.other}`;
}

async function buildThematicDoc(opts: {
  meta: AutodocMeta;
  scanByControl: ScanMap;
  title: string;
  subtitle: string;
  purpose: string;
  audience: string;
  scopeNote: string;
  match: RegExp;
  extraSections?: (Paragraph | Table)[];
}): Promise<Document> {
  const { meta, scanByControl, match } = opts;
  const m365Rows  = pickRows(M365_CONTROLS, match, scanByControl);
  const azureRows = pickRows(AZURE_CONTROLS, match, scanByControl);

  const cover = await buildCover({
    brandLine: 'NIS365 AUDITOR · AutoDoc',
    title:     opts.title,
    subtitle:  opts.subtitle,
    orgName:   meta.orgName,
    logo:      meta.logo,
    footnote:  'Document généré automatiquement à partir du dernier scan Microsoft 365 et Azure.',
  });
  const intro = buildIntro({ purpose: opts.purpose, audience: opts.audience, scope: opts.scopeNote });

  const body: (Paragraph | Table)[] = [
    ...(opts.extraSections ?? []),
    // --- Microsoft 365 ---
    new Paragraph({ text: 'Contrôles liés — Microsoft 365', heading: HeadingLevel.HEADING_1, pageBreakBefore: true }),
    para(summarize(m365Rows), 18),
    new Paragraph({ children: [] }),
    ...(m365Rows.length > 0 ? [controlTable(m365Rows)] : [para('Aucun contrôle Microsoft 365 correspondant dans le catalogue.')]),
    new Paragraph({ children: [] }),
    // --- Azure ---
    new Paragraph({ text: 'Microsoft Azure', heading: HeadingLevel.HEADING_1, pageBreakBefore: true }),
    para(summarize(azureRows), 18),
    new Paragraph({ children: [] }),
    ...(azureRows.length > 0 ? [controlTable(azureRows)] : [para('Aucun contrôle Azure correspondant (ou aucune souscription connectée).')]),
    new Paragraph({ children: [] }),
    para('Note : « Non couvert par le dernier scan » signifie que le contrôle n\'a pas de résultat récent — lancez un scan M365/Azure pour l\'alimenter.', 16),
  ];

  return new Document({
    creator: 'NIS365 Auditor',
    title: `${opts.title} - ${meta.orgName}`,
    features: { updateFields: true },
    styles: AUTODOC_STYLES,
    sections: [{ children: [...cover, ...buildToc(), ...intro, ...body] }],
  });
}

// ===========================================================================
//  1. Configuration MFA actuelle
// ===========================================================================
const MFA_MATCH = /mfa|multifac|authentif|authentication|conditional access|acc[eè]s conditionnel|legacy auth|auth.*legacy|modern auth|passwordless|fido2|windows hello|\bhello\b|sspr|libre-service|break.?glass|device code|sign-?in frequency|authentication strength|authentication context|privileged identity|\bpim\b/i;

function yesNo(b?: boolean | null): string { return b === true ? 'Oui' : b === false ? 'Non' : '—'; }

/** Sections "live" (Graph) : config globale, export utilisateurs, export CA policies. */
function buildMfaFactSections(facts?: MfaFacts): (Paragraph | Table)[] {
  if (!facts) {
    return [
      new Paragraph({ text: "Configuration globale de l'authentification", heading: HeadingLevel.HEADING_1, pageBreakBefore: true }),
      para('Données live indisponibles (permissions Graph ou tenant non connecté) — le document se limite aux contrôles du dernier scan.', 18),
    ];
  }
  const out: (Paragraph | Table)[] = [];
  const sd = facts.securityDefaultsEnabled;
  const ca = facts.conditionalAccess;
  const reg = facts.mfaRegistration;

  // 1. Configuration globale
  out.push(new Paragraph({ text: "Configuration globale de l'authentification", heading: HeadingLevel.HEADING_1, pageBreakBefore: true }));
  const strat = sd === true
    ? 'Security Defaults (MFA imposé à tous, non granulaire)'
    : (ca && ca.mfaEnforcing > 0 ? 'Conditional Access (MFA ciblé par politique)' : 'Aucune application MFA détectée (risque)');
  const globalRows: [string, string][] = [
    ['Security Defaults', sd === true ? 'Activés' : sd === false ? 'Désactivés' : 'Inconnu'],
    ['Stratégie MFA effective', strat],
    ['Politiques d’accès conditionnel', ca ? `${ca.enabled}/${ca.count} activées, dont ${ca.mfaEnforcing} imposant le MFA` : '—'],
    ['Couverture MFA (comptes actifs)', reg ? `${reg.registered}/${reg.total} (${reg.percent} %)` : '—'],
    ['Admins avec MFA enregistré', reg ? `${reg.adminRegistered}/${reg.adminTotal}` : '—'],
  ];
  out.push(new Table({ width: { size: 100, type: WidthType.PERCENTAGE }, rows: globalRows.map(([k, v]) => new TableRow({ children: [headerCell(k, 34), cell(v)] })) }));
  out.push(new Paragraph({ children: [] }));

  // 2. Export utilisateurs + statut MFA
  out.push(new Paragraph({ text: 'Utilisateurs et statut MFA', heading: HeadingLevel.HEADING_1, pageBreakBefore: true }));
  if (reg && reg.users.length > 0) {
    if (reg.listed < reg.total) out.push(para(`${reg.listed} utilisateurs listés sur ${reg.total} (tri : comptes sans MFA d'abord).`, 16));
    out.push(new Table({ width: { size: 100, type: WidthType.PERCENTAGE }, rows: [
      new TableRow({ tableHeader: true, children: [headerCell('Utilisateur', 38), headerCell('Admin', 10), headerCell('MFA enregistré', 16), headerCell('Méthodes', 24), headerCell('SSPR', 12)] }),
      ...reg.users.map((u) => new TableRow({ children: [
        cell(u.userPrincipalName),
        cell(yesNo(u.isAdmin), { bold: u.isAdmin }),
        cell(yesNo(u.isMfaRegistered), { bold: true, color: u.isMfaRegistered ? '1F7A45' : 'B82329' }),
        cell(u.methods || '—'),
        cell(yesNo(u.isSsprRegistered)),
      ] })),
    ] }));
  } else {
    out.push(para('Registre MFA indisponible (permission Reports.Read.All / AuditLog.Read.All manquante).'));
  }
  out.push(new Paragraph({ children: [] }));

  // 3. Export des Conditional Access policies
  out.push(new Paragraph({ text: "Politiques d'accès conditionnel", heading: HeadingLevel.HEADING_1, pageBreakBefore: true }));
  if (ca && ca.items.length > 0) {
    out.push(new Table({ width: { size: 100, type: WidthType.PERCENTAGE }, rows: [
      new TableRow({ tableHeader: true, children: [headerCell('Nom', 34), headerCell('État', 12), headerCell('Exige MFA', 12), headerCell('Cible', 22), headerCell('Clients', 20)] }),
      ...ca.items.map((p) => new TableRow({ children: [
        cell(p.displayName),
        cell(p.state, { bold: p.state === 'enabled', color: p.state === 'enabled' ? '1F7A45' : '7A7A7A' }),
        cell(yesNo(p.requiresMfa), { bold: true, color: p.requiresMfa ? '1F7A45' : '7A7A7A' }),
        cell(p.userScope || '—'),
        cell(p.clientAppTypes || '—'),
      ] })),
    ] }));
  } else {
    out.push(para('Aucune politique d’accès conditionnel (le tenant repose sur les Security Defaults si activés).'));
  }
  out.push(new Paragraph({ children: [] }));

  return out;
}

export async function downloadMfaDocx(params: { meta: AutodocMeta; scanByControl: ScanMap; mfaFacts?: MfaFacts }): Promise<void> {
  const doc = await buildThematicDoc({
    meta: params.meta,
    scanByControl: params.scanByControl,
    title: 'Configuration MFA actuelle',
    subtitle: 'Authentification forte & accès conditionnel — Microsoft 365 / Azure',
    purpose: "Documenter l'état actuel de l'authentification multifacteur : stratégie globale (Security Defaults / Conditional Access), export des utilisateurs et de leur statut MFA, et export des politiques d'accès conditionnel.",
    audience: 'RSSI, équipe IAM/identité, auditeur.',
    scopeNote: params.meta.tenantDomain ? `Tenant ${params.meta.tenantDomain} + souscription(s) Azure connectée(s).` : 'Tenant Microsoft 365 + souscription(s) Azure connectée(s).',
    match: MFA_MATCH,
    extraSections: buildMfaFactSections(params.mfaFacts),
  });
  saveAs(await Packer.toBlob(doc), `Configuration_MFA_${safeName(params.meta.orgName)}_${new Date().toISOString().slice(0, 10)}.docx`);
}

// ===========================================================================
//  2. État du chiffrement des données
// ===========================================================================
const ENCRYPTION_MATCH = /chiffr|encrypt|\btls\b|\bcmk\b|customer.?managed|customer key|customer lockbox|\btde\b|bitlocker|sensitivity label|information protection|\birm\b|message encryption|purge protection|soft delete|https only|\bftps?\b|au repos|en transit|at rest|in transit/i;

export async function downloadEncryptionDocx(params: { meta: AutodocMeta; scanByControl: ScanMap }): Promise<void> {
  const doc = await buildThematicDoc({
    meta: params.meta,
    scanByControl: params.scanByControl,
    title: 'État du chiffrement des données',
    subtitle: 'Chiffrement au repos & en transit — Microsoft 365 / Azure',
    purpose: "Documenter l'état du chiffrement des données au repos et en transit : étiquettes de confidentialité avec chiffrement, IRM/Message Encryption, Customer Key/Lockbox, TLS, clés gérées par le client (CMK), TDE, BitLocker, protection des Key Vaults.",
    audience: 'RSSI, DPO, équipe cloud/infrastructure, auditeur.',
    scopeNote: params.meta.tenantDomain ? `Tenant ${params.meta.tenantDomain} + souscription(s) Azure connectée(s).` : 'Tenant Microsoft 365 + souscription(s) Azure connectée(s).',
    match: ENCRYPTION_MATCH,
  });
  saveAs(await Packer.toBlob(doc), `Etat_Chiffrement_${safeName(params.meta.orgName)}_${new Date().toISOString().slice(0, 10)}.docx`);
}
